import React, { useMemo, useRef, useState } from "react"
import { getFilteredTableData } from "../../../../../shared/utils/tableUtils"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import {
    deleteBooking,
    FetchUserResourceBookingResponse,
    UpcomingOrdersResourceBooking,
} from "../../../../nonAdminUser/reports/upcomingOrders/resourceBookings/userResourceBookingApi"
import { useAppSelector } from "../../../../store/storeHooks"
import { DatePeriod } from "../../../../../shared/components/filters/selectDatePeriodFilter/SelectDatePeriodFilter"
import { useInitialDateFilters } from "../../../../../shared/components/table/useInitialDateFilters"
import { selectUserId } from "../../../../login/loginSelectors"
import { ResourceBooking } from "../../../../nonAdminUser/reports/upcomingOrders/resourceBookings/userResourceBookingTypes"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { useApiQueryTable } from "../../../../../core/api/components/ApiQueryTable"
import { COLUMN_SIZE } from "../../../../../shared/constants/table/config"
import { MRT_ColumnDef, MRT_TableInstance } from "material-react-table"
import { StandardTwoLinesCell } from "../../../../../shared/components/table/StandardTwoLinesCell"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Box, Tooltip } from "@mui/material"
import { StandardChip } from "../../../../../shared/components/badges/StandardChip"
import { StandardTableFilter } from "../../../../../shared/components/table/StandardTableFilters"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { TableNumberOfItemsBox, TableSummaryWrap } from "../../../../../shared/components/table/TableSummaryBox"
import { ConfirmModal } from "../../../../../shared/components/dialogs/ConfirmModal"
import { makeStyles } from "tss-react/mui"
import { tailwindPalette } from "../../../../theme/tailwindPalette"
import { faArrowsRotate, faUserGroup } from "@fortawesome/pro-light-svg-icons"
import { fetchServiceCenterResourceBooking, ServiceCenterMeetingServiceOrdersQuery } from "../meetingServiceApi"
import { MeetingCardMenu } from "../../../../nonAdminUser/reports/home/components/upcomingOrders/meetings/MeetingCardMenu"
import { MenuMeeting } from "../../../../nonAdminUser/reports/home/weekMenuTypes"
import { TimeOrDatePicker } from "../../../../../shared/components/table/TimeOrDatePicker"

const useStyles = makeStyles()(() => ({
    table: {
        ".MuiTableCell-root": {
            paddingLeft: "1rem",
            paddingRight: "1rem",
        },
        ".Mui-TableHeadCell-Content-Labels": {
            paddingLeft: 0,
        },
    },
    wrapIconText: {
        marginRight: ".5rem",
        color: tailwindPalette.slate[500],
    },
}))

const selectUpcomingOrders = (data: FetchUserResourceBookingResponse) => data?.orders || []

export function UserResourceBooking() {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const userId = useAppSelector(selectUserId)

    const [isDatePicker, setIsDatePicker] = useState<boolean>(false)
    const [datePeriod, setDatePeriod] = useState<DatePeriod>(DatePeriod.ALL)
    const { startDate, endDate } = useInitialDateFilters()
    const [dateState, setDateState] = useState({
        startDate: startDate,
        endDate: endDate,
    })

    const [showConfirmModalForRemove, setShowConfirmModalForRemove] = useState(false)
    const [selectedBooking, setSelectedBooking] = useState<ResourceBooking | null>()
    const tableInstanceRef = useRef<MRT_TableInstance<ResourceBooking>>(null)

    const upcomingOrdersResult = useApiQuery(fetchServiceCenterResourceBooking, {
        queryName: ServiceCenterMeetingServiceOrdersQuery.RESOURCE_BOOKING,
        dependencies: {
            userId: userId ?? 0,
            period: isDatePicker ? undefined : datePeriod,
            startDate: isDatePicker ? dateState.startDate?.toISODate() : undefined,
            endDate: isDatePicker ? dateState.endDate?.toISODate() : undefined,
        },
        select: selectUpcomingOrders,
    })

    const { mutate: callDeleteBooking } = useApiMutation(deleteBooking, {
        async onSuccess(_params, _data, context) {
            await context.queryClient.invalidateQueries({
                queryKey: [UpcomingOrdersResourceBooking.UPCOMING_ORDERS_RESOURCE_BOOKING],
            })
        },
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(upcomingOrdersResult)

    const columns = useMemo<MRT_ColumnDef<ResourceBooking>[]>(
        () => [
            columnFactory.idColumn({
                header: l10n.getString("user-upcoming-orders-resource-booking-table-id"),
                accessorKey: "id",
            }),
            columnFactory.datetimeCompactPeriod(
                {
                    header: l10n.getString("user-upcoming-orders-resource-booking-table-time"),
                    accessorKey: "meetingDetails.startDateTime",
                },
                (row) => row.meetingDetails.startDateTime,
                (row) => row.meetingDetails.endDateTime,
            ),
            columnFactory.nameColumn({
                header: l10n.getString("meeting-service-table-meeting-name-column"),
                accessorKey: "meetingDetails",
                size: COLUMN_SIZE.XL,
                Cell: ({ row }) => {
                    return (
                        <StandardTwoLinesCell
                            title={row.original.meetingDetails.meetingName}
                            subtitle1={
                                <>
                                    <FontAwesomeIcon icon={faUserGroup} className={classes.wrapIconText} />
                                    {row.original.orderSummary.participants}
                                </>
                            }
                            subtitle2={<FontAwesomeIcon icon={faArrowsRotate} className={classes.wrapIconText} />}
                        />
                    )
                },
            }),
            columnFactory.nameColumn({
                header: l10n.getString("meeting-service-table-location-column"),
                accessorKey: "meetingDetails.location",
            }),
            columnFactory.statusColumn({
                header: l10n.getString("user-upcoming-orders-resource-booking-table-visibility"),
                id: "visibility",
                accessorFn: (row) => (row.meetingDetails.isPrivate ? "yes" : "no"),
                Cell: ({ row }) => {
                    return (
                        <Tooltip
                            arrow
                            title={
                                row.original.meetingDetails.isPrivate
                                    ? l10n.getString(
                                          "user-upcoming-orders-resource-booking-table-visibility-private-tooltip",
                                      )
                                    : l10n.getString(
                                          "user-upcoming-orders-resource-booking-table-visibility-public-tooltip",
                                      )
                            }
                        >
                            <div>
                                {!row.original.meetingDetails.isPrivate ? (
                                    <StandardChip
                                        label={l10n.getString(
                                            "user-upcoming-orders-resource-booking-table-visibility-public",
                                        )}
                                        variant="success"
                                        placement="table"
                                    />
                                ) : (
                                    <StandardChip
                                        label={l10n.getString(
                                            "user-upcoming-orders-resource-booking-table-visibility-private",
                                        )}
                                        variant="red"
                                        placement="table"
                                    />
                                )}
                            </div>
                        </Tooltip>
                    )
                },
            }),
        ],
        [l10n, columnFactory],
    )

    const handleChangeTimeOrDatePicker = () => {
        setIsDatePicker((prev) => !prev)
    }

    const mapBookingToMeeting = (booking: ResourceBooking): MenuMeeting => {
        const { meetingName, startDateTime, endDateTime } = booking.meetingDetails

        return {
            id: booking.id,
            uid: booking.uid,
            orderSummary: booking.orderSummary,
            organizers: booking.organizers,
            meetingDetails: {
                meetingName,
                startDateTime,
                endDateTime,
            },
        }
    }

    return (
        <>
            <Box className={classes.table}>
                <ApiQueryTable
                    tableInstanceRef={tableInstanceRef}
                    columns={columns}
                    initialState={{ density: "spacious" }}
                    renderRowActionMenuItems={({ row, closeMenu }) => [
                        <MeetingCardMenu
                            ignoreMenuContainer
                            menuOnApi
                            meetings={getFilteredTableData(tableInstanceRef)}
                            meeting={mapBookingToMeeting(row.original)}
                            handleMenuClose={closeMenu}
                        />,
                    ]}
                    renderTopToolbarCustomActions={() => (
                        <StandardTableFilter variant="left">
                            <TimeOrDatePicker
                                isDatePicker={isDatePicker}
                                dateState={dateState}
                                setDateState={setDateState}
                                timePeriod={datePeriod}
                                handleChangeTimePeriod={setDatePeriod}
                                handleChangeTimeOrDatePicker={handleChangeTimeOrDatePicker}
                            />
                        </StandardTableFilter>
                    )}
                    renderToolbarInternalActions={({ table }) => <StandardTableFilter variant="right" table={table} />}
                    renderBottomToolbarCustomActions={({ table }) => (
                        <TableSummaryWrap>
                            <LocalizedStrict id="table-summary-box-number-of-orders" attrs={{ title: true }}>
                                <TableNumberOfItemsBox
                                    data={table.getFilteredRowModel().rows}
                                    isLoading={upcomingOrdersResult.isLoading}
                                    title="Number of orders"
                                />
                            </LocalizedStrict>
                        </TableSummaryWrap>
                    )}
                />
            </Box>

            {showConfirmModalForRemove && selectedBooking?.id && (
                <LocalizedStrict
                    id="user-upcoming-orders-resource-booking-table-remove-dialog"
                    attrs={{ title: true, message: true, buttonLabelSubmit: true }}
                >
                    <ConfirmModal
                        open={showConfirmModalForRemove}
                        onClose={() => setShowConfirmModalForRemove(false)}
                        title="Remove booking"
                        message="Are you sure that you want to remove booking?"
                        buttonLabelSubmit="Remove"
                        handleClick={() => callDeleteBooking({ bookingId: selectedBooking.id })}
                    />
                </LocalizedStrict>
            )}
        </>
    )
}
